<script setup lang="ts">
import type { Component } from 'vue'
import { RouterLink } from 'vue-router'

import { hapticTab } from '@/composables/useHaptics'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { useScrollContainer } from '@/composables/useScrollContainer'

defineProps<{
  /** nom de route cible */
  to: string
  label: string
  icon: Component
}>()

const scroller = useScrollContainer()
const prefersReducedMotion = useReducedMotion()

/**
 * Le tap sur un onglet, avant la navigation.
 *
 * Deux comportements, ceux d'une tab bar native :
 *
 * - onglet **différent** → un retour haptique léger (natif seulement, cf.
 *   `useHaptics`) puis la navigation ;
 * - onglet **déjà actif** → pas d'haptique (rien ne change) mais un retour en
 *   haut de la page. Sans lui, ce tap ne ferait absolument rien : vue-router
 *   abandonne une navigation vers la route courante, et depuis que la position
 *   de défilement est mémorisée (`App.vue`) la page ne se remet plus à zéro
 *   toute seule. C'est aussi, avec le bouton « Retour », la seule commande de
 *   remontée atteignable au POUCE (HIG `designing-for-ios` : « easier to reach
 *   a control … in the middle or bottom area ») — la pastille « Retour » et le
 *   menu « … », eux, sont en haut de l'écran.
 *
 * Depuis une **sous-page**, il n'y a rien de particulier à faire : le lien
 * pointe sur la route de l'onglet, que la sous-page n'est pas (c'est `meta.tab`
 * qui garde la bulle allumée, pas la route) — la navigation a donc bien lieu,
 * et elle ramène à la racine de l'onglet.
 */
function tap(isActive: boolean): void {
  if (!isActive) {
    hapticTab()
    return
  }

  scroller.value?.scrollTo({
    top: 0,
    behavior: prefersReducedMotion.value ? 'auto' : 'smooth',
  })
}
</script>

<template>
  <!--
    `custom` nous donne `isActive` calculé par vue-router (gère aussi les
    routes enfants), sans avoir à comparer les chemins à la main.
  -->
  <RouterLink v-slot="{ href, navigate, isActive }" :to="{ name: to }" custom>
    <a
      :href="href"
      class="tab-item"
      :class="{ 'is-active': isActive }"
      :aria-current="isActive ? 'page' : undefined"
      @click="tap(isActive), navigate($event)"
    >
      <span class="tab-item__icon">
        <component :is="icon" :active="isActive" />
      </span>
      <span class="tab-item__label">{{ label }}</span>
    </a>
  </RouterLink>
</template>

<style scoped>
.tab-item {
  flex: 1 1 0;
  min-width: 0;
  /* au-dessus de la bulle glissante de la tab bar */
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;

  height: 100%;
  padding: 0 2px;
  /* même rayon concentrique que la bulle glissante de <TabBar> : l'item se
     superpose exactement à elle (rayon de la barre moins son padding). */
  border-radius: calc(var(--tabbar-radius) - var(--tabbar-inset));

  /* secondary plutôt que tertiary : lisibilité du libellé sur le verre opaque
     de la tab bar, dont du contenu peut passer dessous au scroll */
  color: var(--color-text-secondary);
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.2s var(--ease-ios);
}

/* pas de fond ici : c'est la bulle glissante de <TabBar> qui le porte.
   L'accent sur du VERRE, pas sur une surface : `--color-accent-text` n'y
   donnait que 3,30:1 au pire cas de teinte, une jaquette sombre défilant sous
   la barre (cf. le token dans `variables.css`). */
.tab-item.is-active {
  color: var(--color-on-material-accent);
}

/* feedback tactile : léger enfoncement, comme une tab bar native */
.tab-item:active .tab-item__icon {
  transform: scale(0.88);
}

.tab-item__icon {
  display: flex;
  width: 25px;
  height: 25px;
  transition: transform 0.25s var(--ease-ios);
}

.tab-item.is-active .tab-item__icon {
  transform: scale(1.06);
}

.tab-item__icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.tab-item__label {
  /* Plancher HIG : 11pt minimum pour un libellé (10,5px était sous le seuil).

     Et un PLAFOND, le seul de l'app : la tab bar est une géométrie fixe
     (`--tabbar-height` 60px, moins 2 × `--tabbar-inset` = 52px utiles, dont
     25px d'icône et 3px de `gap`). Le libellé dispose donc de 24px : à
     Dynamic Type 150 %, 0.6875rem vaut déjà 16,5px, soit 18,2px de ligne —
     et à 200 % il fait sauter la barre. `min(0.6875rem, 13px)` le laisse
     grandir jusqu'à 13px (14,3px de ligne) puis s'arrête.

     Ce n'est pas un renoncement à Dynamic Type : iOS fait la même chose en
     plus radical, en MASQUANT les libellés de sa tab bar aux tailles AX. Le
     reste de l'app, lui, n'a aucun plafond. */
  font-size: min(0.6875rem, 13px);
  font-weight: 600;
  letter-spacing: -0.1px;
  line-height: 1.1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
